// Display formatters shared by dashboard widgets.

export const formatNumber = (n: number) => new Intl.NumberFormat().format(Math.round(n || 0));

export const formatCompact = (n: number) =>
  new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n || 0);

export const formatCurrency = (n: number, currency = "USD") => {
  const v = n || 0;
  // Sub-dollar LLM spend is common; keep enough precision to be meaningful.
  const digits = v !== 0 && Math.abs(v) < 1 ? 4 : 2;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: digits,
  }).format(v);
};

/** Percentage change from `previous` to `current`, or null when there is no baseline. */
export const percentChange = (current: number, previous: number): number | null => {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
};

export const formatDayLabel = (isoDay: string, long = false) => {
  const [y, m, d] = isoDay.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return long
    ? date.toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "short", year: "numeric" })
    : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const formatWeekday = (isoDay: string) => {
  const [y, m, d] = isoDay.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short" });
};

export const formatDateTime = (iso: string) =>
  iso
    ? new Date(iso).toLocaleString("en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : "";

const PROVIDER_NAMES: Record<string, string> = {
  openai: "OpenAI",
  azure_openai: "Azure OpenAI",
  azure: "Azure",
  anthropic: "Anthropic",
  claude: "Claude",
  gemini: "Gemini",
  google: "Google",
  mistral: "Mistral",
  cohere: "Cohere",
  deepseek: "DeepSeek",
  groq: "Groq",
  xai: "xAI",
  meta: "Meta",
  nvidia: "NVIDIA",
  unknown: "Unknown",
};

export const providerName = (id: string) => PROVIDER_NAMES[id?.toLowerCase()] || id || "Unknown";

export const initials = (name: string) =>
  (name || "?")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
